import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Modal,
  OutlinedInput,
  styled,
  TablePagination,
} from "@mui/material";
import TableHeader from "../table/TableHeader";
import MainTableBody from "../table/MainTableBody";
import CustomTable from "../table/CustomTable";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import TemplateTableRow from "./TemplateTableRow";
import { Add, CloseOutlined, PlusOne } from "@mui/icons-material";
import useAuthFetch from "../../hooks/useAuthFetch";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmBox from "../ConfirmBox";
import toast from "react-hot-toast";
import FilterDropdown from "../filter/FilterDropdown";
import Cookies from "js-cookie";

export const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 8,
  paddingBottom: 8,
};

export const CustomizeTextField = styled(OutlinedInput)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

// Face verification settings profiles for the association selects. The list
// endpoint returns `{ profiles, defaults }`; accept a bare array too.
const fetchVerificationProfileOptions = async (customFetch) => {
  try {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/verificationProfile/list`
    );
    const { response, status } = await customFetch(url, "GET");
    if (status !== 200) {
      console.log("Error in fetching verification profiles");
      return [];
    }
    if (Array.isArray(response)) return response;
    return Array.isArray(response?.profiles) ? response.profiles : [];
  } catch (error) {
    console.error("Error in fetching verification profiles:", error);
    return [];
  }
};

// "Default settings" (empty value, saved as null) follows the IS_DEFAULT
// profile, so later edits to the default apply automatically. The default row
// itself is left out of the options so it isn't linked explicitly by mistake,
// unless this association already links it.
const VerificationProfileSelect = ({ value, onChange, profiles }) => {
  const defaultProfile = profiles?.find((p) => p.isDefault);
  const options = profiles?.filter(
    (p) => !p.isDefault || p.profileId === value
  );
  return (
    <div className="mt-4">
      <p className="text-black-150 text-sm ml-1 mb-1">Face Verification Settings</p>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-4 bg-[#F3F4F8] text-black w-full  rounded-md focus:outline-none text-black-150  "
      >
        <option value="">
          {defaultProfile ? `Default (${defaultProfile.name})` : "Default settings"}
        </option>
        {options?.map(({ profileId, name }) => (
          <option key={profileId} value={profileId}>
            {name}
          </option>
        ))}
      </select>
    </div>
  );
};

export const CreateAssociationModal = ({ refetch, setOpenErr, setErrMsg }) => {
  const customFetch = useAuthFetch();
  const [agentCode, setAgentCode] = React.useState();
  const [templateId, setTemplateId] = React.useState();
  const [templateName, setTemplateName] = React.useState();
  const [qnaTemplateId, setQnaTemplateId] = React.useState();
  const [verificationProfileId, setVerificationProfileId] = React.useState("");
  const [verificationProfiles, setVerificationProfiles] = useState([]);
  const [agentCodeErr, setAgentCodeErr] = useState(false)

  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchQnaTemplates();
  }, []);

  const [templates, setTemplates] = useState(null);
  const [qnaTemplates, setQnaTemplates] = useState(null);

  const fetchTemplates = async () => {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/template/fetchAll`
    );
    const method = "GET"
    const { response, status } = await customFetch(
      url,
      method,
    );

    if (status === 200) {
      setTemplates(response);
    } else {
    }
  };

  const fetchQnaTemplates = async () => {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/list`
    );
    url.searchParams.append("perPage", 1000);

    const { response, status } = await customFetch(url, "GET");

    if (status === 200) {
      setQnaTemplates(response?.data || []);
    } else {
      console.log("Error in fetching qna templates");
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setVerificationProfiles(await fetchVerificationProfileOptions(customFetch));
  };
  const handleClose = () => setOpen(false);

  async function createAssociation({ agentCode, templateId, templateName }) {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/agentAssociation/create`
    );
    const bodyContent = {
      agentCode: agentCode,
      templateId: templateId,
      templateName: templateName,
      qnaTemplateId: qnaTemplateId,
      verificationProfileId: verificationProfileId || null,
    }
    const method = "POST"
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );


    if (status === 201) {
      toast.success("Agent-Template Association Created Succesfully!", {
        position: "bottom-right",
      });
      setAgentCode("");
      setTemplateId("");
      setTemplateName("");
      setQnaTemplateId("");
      setVerificationProfileId("");
      refetch();
    } else {
      setOpenErr(true);
      setErrMsg(
        message
          ? message
          : "Something went wrong while creating agent-template association!"
      );
    }
    handleClose();
  }

  return (
    <div className="">
      <button
        onClick={handleOpen}
        className=" bg-purple-300 flex items-center py-2 pl-2 pr-3 text-white font-bold rounded"
      >
        <Add fontSize="small" className="ml-0 pl-0 " />
        Create
      </button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="rounded-xl relative">
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
          </button>
          <p className="text-2xl mb-0 font-extrabold text-center text-gray-850">
            Create New Agent-Template Association
          </p>
          <div className="mt-9">
            <FormControl variant="outlined" className="w-full">
              <CustomizeTextField
                id="outlined-adornment-weight"
                aria-describedby="outlined-weight-helper-text"
                type="number"
                inputProps={{
                  "aria-label": "weight",
                }}
                placeholder="Enter Agent Code (fsc_um_code)"
                className="bg-[#F3F4F8] text-black w-full  rounded-lg "
                value={agentCode}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value) && value.length <= 12) {
                    setAgentCode(value);
                  }
                }}
              />
            </FormControl>
            {agentCodeErr && (
              <p className="text-red-500 text-sm mt-1">
                Agent Code is required, must contain only numbers, and can be up to 12 characters long.
              </p>
            )}
          </div>

          <div className="mt-4">
            <select
              value={templateId}
              onChange={(e) => {
                const selectedId = e.target.value;

                const selectedTemplate = templates.find(
                  (template) => String(template.templateId) === selectedId
                );

                if (selectedTemplate) {
                  setTemplateName(selectedTemplate.templateName);
                  setTemplateId(selectedTemplate.templateId);
                }
              }}
              className="px-2 py-4 bg-[#F3F4F8] text-black w-full  rounded-md focus:outline-none text-black-150  "
            >
              <option value="">Choose a template</option>
              {templates?.map(({ templateName, templateId }) => (
                <option key={templateId} value={templateId}>
                  {templateName}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <select
              value={qnaTemplateId}
              onChange={(e) => setQnaTemplateId(e.target.value)}
              className="px-2 py-4 bg-[#F3F4F8] text-black w-full  rounded-md focus:outline-none text-black-150  "
            >
              <option value="">Choose a QnA template</option>
              {qnaTemplates?.map(({ name, qnaTemplateId }) => (
                <option key={qnaTemplateId} value={qnaTemplateId}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <VerificationProfileSelect
            value={verificationProfileId}
            onChange={setVerificationProfileId}
            profiles={verificationProfiles}
          />

          <div className="mt-4 flex items-center justify-center">
            <button
              className="bg-purple-300 py-3 w-full text-white text-base font-bold rounded-lg"
              onClick={async () => {
                if (!/^\d+$/.test(agentCode) || agentCode.length === 0 || agentCode.length > 12) {
                  setAgentCodeErr(true)
                  return;
                } else {
                  setAgentCodeErr(false)
                }
                await createAssociation({
                  agentCode: agentCode,
                  templateId: templateId,
                  templateName: templateName,
                  qnaTemplateId: qnaTemplateId,
                });
              }}
            >
              Create
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

const EditAgentModal = ({
  setErrMsg,
  open,
  setOpen,
  row,
  refetch,
  setOpenErr,
}) => {
  const customFetch = useAuthFetch();
  const [agentCode, setAgentCode] = React.useState(row?.agentCode);
  const [templateId, setTemplateId] = React.useState(row?.templateId);
  const [templateName, setTemplateName] = React.useState(row?.templateName);
  const [qnaTemplateId, setQnaTemplateId] = React.useState(row?.qnaTemplateId);
  const [verificationProfileId, setVerificationProfileId] = React.useState(
    row?.verificationProfileId || ""
  );
  const [verificationProfiles, setVerificationProfiles] = useState([]);

  useEffect(() => {
    fetchTemplates();
    fetchQnaTemplates();
    fetchVerificationProfileOptions(customFetch).then(setVerificationProfiles);
  }, []);

  const [templates, setTemplates] = useState(null);
  const [qnaTemplates, setQnaTemplates] = useState(null);

  const fetchTemplates = async () => {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/template/fetchAll`
    );
    const method = "GET"
    const { response, status } = await customFetch(
      url,
      method,
    );

    if (status === 200) {
      setTemplates(response);
    } else {
      console.log("Error in fetching templates");
    }
  };

  const fetchQnaTemplates = async () => {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/list`
    );
    url.searchParams.append("perPage", 1000);

    const { response, status } = await customFetch(url, "GET");

    if (status === 200) {
      setQnaTemplates(response?.data || []);
    } else {
      console.log("Error in fetching qna templates");
    }
  };

  async function updateTemplate({ templateId, templateName }) {
    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/agentAssociation/update`
    );

    const bodyContent = {
      agentCode: row?.agentCode,
      templateId: templateId,
      templateName: templateName,
      qnaTemplateId: qnaTemplateId,
      verificationProfileId: verificationProfileId || null,
    }
    const method = "POST"
    const { response, status, message } = await customFetch(
      url,
      method,
      bodyContent
    );

    if (status === 200) {
      toast.success("Agent-Template Association Updated Succesfully!", {
        position: "bottom-right",
      });
      refetch();
    } else {
      setOpenErr(true);
      setErrMsg(
        message
          ? message
          : "Something went wrong while updating agent-template Association!"
      );
    }
    handleClose();
  }

  const handleClose = () => setOpen(false);

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
            paddingBottom: 8,
          }}
          className="rounded-xl relative"
        >
          <button className="absolute top-2 right-3" onClick={handleClose}>
            <CloseOutlined className="w-5 h-5  text-gray-450 hover:text-black" />
          </button>
          <p className="text-2xl mb-0 font-extrabold text-center text-gray-850">
            Edit Agent Template Association
          </p>
          <div className="mt-9">
            <p className="text-black-150 text-lg ml-1 mb-2">Agent Code</p>
            <FormControl variant="outlined" className="w-full">
              <CustomizeTextField
                id="outlined-adornment-weight"
                aria-describedby="outlined-weight-helper-text"
                inputProps={{
                  "aria-label": "weight",
                }}
                disabled
                placeholder="Enter Agent Code (fsc_um_code)"
                className="bg-[#F3F4F8] text-black w-full  rounded-lg"
                value={agentCode}
                onChange={(e) => {
                  setAgentCode(e.target.value);
                }}
              />
            </FormControl>
          </div>

          <div className="mt-4">
            <select
              value={templateId}
              onChange={(e) => {
                const selectedId = e.target.value;

                const selectedTemplate = templates.find(
                  (template) => String(template.templateId) === selectedId
                );

                if (selectedTemplate) {
                  setTemplateName(selectedTemplate.templateName);
                  setTemplateId(selectedTemplate.templateId);
                }
              }}
              className="px-2 py-4 bg-[#F3F4F8] text-black w-full  rounded-md focus:outline-none text-black-150  "
            >
              <option value="">Choose a template</option>
              {templates?.map(({ templateName, templateId }) => (
                <option key={templateId} value={templateId}>
                  {templateName}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <select
              value={qnaTemplateId}
              onChange={(e) => setQnaTemplateId(e.target.value)}
              className="px-2 py-4 bg-[#F3F4F8] text-black w-full  rounded-md focus:outline-none text-black-150  "
            >
              <option value="">Choose a QnA template</option>
              {qnaTemplates?.map(({ name, qnaTemplateId }) => (
                <option key={qnaTemplateId} value={qnaTemplateId}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <VerificationProfileSelect
            value={verificationProfileId}
            onChange={setVerificationProfileId}
            profiles={verificationProfiles}
          />
          <div className="mt-7 flex items-center justify-center">
            <button
              className="bg-purple-300 py-3 px-9 text-white text-base font-bold rounded-lg"
              onClick={async () => {
                await updateTemplate({
                  agentCode: agentCode,
                  templateId: templateId,
                  templateName: templateName,
                });
              }}
            >
              Update
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default function TemplateTable({
  reqState,
  object,
  setObject,
  handleFetch,
  handleFetchWithPage,
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState("");
  const [rowsPerPage, setRowsPerPage] = useState(0);
  const [page, setPage] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [openErr, setOpenErr] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (reqState.response?.pageInfo) {
      setRowsPerPage(reqState.response.pageInfo.perPage);
      setPage(reqState.response.pageInfo.currentPage - 1); // Convert to 0-indexed for MUI
    }
  }, [reqState?.response]);

  const updateQueryParams = (updatedParams) => {
    const searchParams = new URLSearchParams(location.search);

    // Update or remove query parameters based on `updatedParams`
    Object.keys(updatedParams).forEach((key) => {
      if (updatedParams[key] !== undefined && updatedParams[key] !== null) {
        searchParams.set(key, updatedParams[key]);
      } else {
        searchParams.delete(key);
      }
    });

    // Navigate with updated query parameters
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const { isLoading, response } = reqState;

  const refetch = () => {
    // const q = { ...query, page: 1 };
    // if (!q.query) delete q.query;
    // navigate({
    //   pathname: window.location.pathname, // current path
    //   search: `?${new URLSearchParams(q).toString()}`, // query string
    // }, { replace: true });
    handleFetch();
  };

  const columns = ["Agent Code (fsc_um_code)", "Template Id", "Template Name", "Qna Template Name", "Qna Template id", "Verification Settings", "Actions"];

  useEffect(() => {
    if (response?.pageInfo) {
      setRowsPerPage(response?.pageInfo.perPage);
      setPage(response?.pageInfo?.currentPage - 1);
    }
  }, [response]);

  const handleChangePage = (event, newPage) => {
    const updatedPage = newPage + 1; // Material-UI uses 0-based indexing
    updateQueryParams({ page: updatedPage });
    handleFetchWithPage(updatedPage); // Trigger fetch for the new page
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newPerPage);
    setPage(0); // Reset to the first page
    updateQueryParams({ perPage: newPerPage, page: 1 });
  };

  return (
    <>
      <div
        className="py-1 px-4"
        style={{
          height: `calc(100vh - 100px)`,
        }}
      >
        <div className="flex flex-row mb-4 items-center justify-between">
          <p className="text-xl mb-0 font-extrabold">
            Agent-Template Associations ({response?.pageInfo.total})
          </p>
          <div className="flex flex-row">
            <FilterDropdown object={object} setObject={setObject} />
            <button
              onClick={() => refetch()}
              className="border focus:border-blue-600 flex items-center border-[#E0E0E0] mx-2 rounded py-1.5 px-3"
            >
              Refresh Data
            </button>
            {JSON.parse(Cookies.get("userDets")).role === "ADMIN" && (
              <CreateAssociationModal
                setErrMsg={setErrMsg}
                setOpenErr={setOpenErr}
                refetch={refetch}
              />
            )}
          </div>
        </div>
        {!response?.data && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={"Server Error, Please try again later."}
          />
        ) : response?.data?.length === 0 && !isLoading ? (
          <NoDataTable
            columns={columns}
            recordingKeyClassName={true}
            message={
              "No agent-template associations available. Create one to see it here."
            }
          />
        ) : (
          <CustomTable>
            <TableHeader columns={columns} />
            <MainTableBody isLoading={isLoading}>
              {response?.data.length === 0
                ? Array.from({ length: columns.length }).map((i) => (
                  <LoadingTableSkeleton key={i} index={i} />
                ))
                : response?.data.map((row, i) => (
                  <TemplateTableRow
                    key={i}
                    row={row}
                    refetch={refetch}
                    setOpen={setOpen}
                    setSelectedRow={setSelectedRow}
                  />
                ))}
            </MainTableBody>

            {response?.pageInfo && (
              <TablePagination
                className="sticky bottom-0 bg-white"
                rowsPerPageOptions={[20]}
                count={response?.pageInfo.total}
                rowsPerPage={rowsPerPage}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handleChangePage}
              />
            )}
          </CustomTable>
        )}
      </div>
      {open && (
        <EditAgentModal
          setErrMsg={setErrMsg}
          setOpenErr={setOpenErr}
          refetch={refetch}
          open={open}
          setOpen={setOpen}
          row={selectedRow}
        />
      )}
      <ConfirmBox
        open={openErr}
        setOpen={setOpenErr}
        buttonText={"Close"}
        text={errMsg}
      />
    </>
  );
}
